/**
 * Skills + admin actions. The admin governance surface (TRE-780) exposes no
 * "list every skill" endpoint — the authoring/palette side owns that. What it does
 * expose, and what an admin acts on here, is the set of **currently-held checkout
 * locks** (/admin/locks): break another user's lock (force-release, showing the
 * holder) or permanently remove a skill (hard-delete, type-to-confirm — irreversible).
 *
 * Force-release and hard-delete are gated on the caller's capabilities; the server
 * re-checks. After any mutation we re-read /admin/locks (server response is truth).
 */

import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { AccountContext, Lock } from "../api/types";
import { LockedChip } from "../components/Chips";
import { Confirm } from "../components/Confirm";
import { can } from "../lib/capabilities";
import { useToast } from "../components/Toast";
import { useAsyncData } from "../lib/useAsyncData";

type Pending =
  | { kind: "force-release"; lock: Lock }
  | { kind: "hard-delete"; lock: Lock }
  | null;

export function Skills({ ctx }: { ctx: AccountContext }) {
  const data = useAsyncData(() => api.locks(), []);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const canForce = can(ctx.capabilities, "force_release_lock");
  const canDelete = can(ctx.capabilities, "manage_trash");

  async function run() {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "force-release") {
        const res = await api.forceRelease(pending.lock.skillId);
        toast.push(
          res.released
            ? `Released lock on ${pending.lock.skillId} (was held by ${res.priorHolder ?? "?"}).`
            : `${pending.lock.skillId} was already unlocked.`,
        );
      } else {
        await api.hardDelete(pending.lock.skillId);
        toast.push(`Permanently deleted ${pending.lock.skillId}.`);
      }
      setPending(null);
      data.reload();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "The action failed.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Skills &amp; admin actions</h1>
      <p className="screen-intro">
        Checkout locks currently held across the account. Break a stuck lock or permanently remove a
        skill. The full skill library lives in the Revit palette; this is the governance surface.
      </p>

      {data.loading && <p className="muted">Loading locks…</p>}
      {data.error && (
        <div className="banner banner--error" role="alert">
          {data.error} <button className="btn--link" onClick={data.reload}>Retry</button>
        </div>
      )}

      {data.value && data.value.count === 0 && (
        <div className="banner banner--info">No checkout locks are held right now.</div>
      )}

      {data.value && data.value.count > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Skill</th>
              <th>Status</th>
              <th>Held by</th>
              <th>Since</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data.value.locks.map((l) => (
              <tr key={l.skillId}>
                <td className="mono">{l.skillId}</td>
                <td>
                  <LockedChip holder={l.holder} />
                </td>
                <td className="mono">{l.holder}</td>
                <td className="muted">{formatDate(l.acquiredAt)}</td>
                <td className="row-actions">
                  {canForce && (
                    <button className="btn btn--small" onClick={() => setPending({ kind: "force-release", lock: l })}>
                      Force-release
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className="btn btn--small btn--danger"
                      onClick={() => setPending({ kind: "hard-delete", lock: l })}
                    >
                      Hard-delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pending?.kind === "force-release" && (
        <Confirm
          title="Force-release lock?"
          body={
            <>
              This breaks the checkout lock on <code className="mono">{pending.lock.skillId}</code>,
              currently held by <strong>{pending.lock.holder}</strong>. Any unsaved edits they have
              won't be affected, but they'll lose the lock.
            </>
          }
          confirmLabel="Force-release"
          busy={busy}
          onConfirm={run}
          onCancel={() => setPending(null)}
        />
      )}
      {pending?.kind === "hard-delete" && (
        <Confirm
          title="Permanently delete skill?"
          tone="danger"
          confirmPhrase={pending.lock.skillId}
          body={
            <>
              This permanently deletes <code className="mono">{pending.lock.skillId}</code>. It cannot
              be restored. Any held lock is freed.
            </>
          }
          confirmLabel="Delete forever"
          busy={busy}
          onConfirm={run}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
